<!--
  Equipment Safety Inspection & Repair Report.

  Header (company, location, job #, equipment #, hour meter, date, shift, job
  name, type, mileage), then the five printed checklist groups in printed
  order, each item a row with an N/A / OK / RR control using the app's
  existing `.segmented` pattern (see app.css and JobSetup.svelte's ITP items
  1-5, which is the other place a raw `.segmented` group is built by hand
  rather than through YesNoField). Then explanation of defects, the two
  repairs lines, and three signature blocks. Follows DailyReportForm.svelte's
  shape: raw string state throughout (every field here is already a plain
  string on the wire), an explicit Save, add/remove/reorder rows per group,
  delete behind an in-page confirmation banner rather than confirm().

  A brand-new inspection seeds the full printed checklist (all 35 items,
  status 'pending') from EQUIPMENT_CHECKLIST — mirrored here on the client
  from the same list the server would seed from if `items` were left out of
  the save body, so the user sees (and can edit) the standard checklist
  before ever saving, exactly as DailyReportForm mirrors its own defaults.

  Items are kept as five separate arrays (one per group) rather than one flat
  list with a group_name column, because reordering only ever makes sense
  within a group on this printed sheet — there is no "move a Fluids item up
  into Engine Compartment" gesture. body() flattens them back into one list,
  group by group in GROUP_ORDER (which is printed order), which is exactly
  the order the server assigns `seq` from.

  The RR count is shown once, near the top of the checklist, as a plain
  count — never coloured and never called a verdict. This form records what
  was observed; the app judges nothing.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { printEquipment } from '../../lib/export/quality-pdf';
  import SitePicker from './SitePicker.svelte';
  // Aliased: this component has its own `location` field, which would
  // otherwise shadow the router store.
  import { createAutosave } from '../../lib/autosave';
  import { claimAddress, linkHandler, location as appLocation, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import {
    EQUIPMENT_CHECKLIST,
    EQUIPMENT_CHECKLIST_GROUP_LABELS,
    EQUIPMENT_CHECKLIST_LEGEND
  } from '../../lib/seed-data';
  import { advanceOnEnter } from '../../lib/fields/advance-on-enter';
  import { isAdmin, notify, notifyError } from '../../lib/store';
  import type {
    EquipmentChecklistGroup,
    EquipmentInspectionItemInput,
    InspectionStatus,
    SaveEquipmentInspectionRequest,
    SiteSummary
  } from '../../lib/types';

  interface Props {
    /** null for a new inspection. */
    equipmentId?: number | null;
  }

  let { equipmentId = null }: Props = $props();

  /** The five printed groups, in printed order — also the order `seq` is assigned in. */
  const GROUP_ORDER: EquipmentChecklistGroup[] = ['outside', 'engine', 'cab', 'cab_continued', 'fluids'];

  const STATUS_CHOICES: { value: InspectionStatus; label: string; cls: string }[] = [
    { value: 'na', label: 'N/A', cls: '' },
    { value: 'ok', label: 'OK', cls: 'is-ok' },
    { value: 'rr', label: 'RR', cls: 'is-fail' }
  ];

  /** One checklist row as held by the form. `pending` is "no status set yet". */
  interface ItemRow {
    label: string;
    status: InspectionStatus;
  }

  function emptyGroups(): Record<EquipmentChecklistGroup, ItemRow[]> {
    return { outside: [], engine: [], cab: [], cab_continued: [], fluids: [] };
  }

  /** A blank sheet starts from the full printed checklist, every item pending. */
  function startingGroups(): Record<EquipmentChecklistGroup, ItemRow[]> {
    const next = emptyGroups();
    for (const item of EQUIPMENT_CHECKLIST) {
      next[item.group] = [...next[item.group], { label: item.label, status: 'pending' }];
    }
    return next;
  }

  let loading = $state(equipmentId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new inspection gets one on its first save. */
  let savedId = $state<number | null>(equipmentId);

  let siteId = $state<number | null>(null);
  let company = $state('');
  let location = $state('');
  let jobNo = $state('');
  let equipmentNo = $state('');
  let hourMeter = $state('');
  let inspectionDate = $state('');
  let shift = $state('');
  let jobName = $state('');
  let equipmentType = $state('');
  let mileage = $state('');
  let defectsExplanation = $state('');
  let repairsNeeded = $state('');
  let repairsNotNeeded = $state('');
  let operatorSignature = $state('');
  let operatorDate = $state('');
  let repairsCompletedBy = $state('');
  let repairsCompletedDate = $state('');
  let mechanicSignature = $state('');
  let mechanicDate = $state('');

  let groups = $state<Record<EquipmentChecklistGroup, ItemRow[]>>(
    equipmentId === null ? startingGroups() : emptyGroups()
  );

  /** Items whose status is 'rr', across every group. Shown once, never coloured. */
  let rrCount = $derived(
    GROUP_ORDER.reduce((sum, group) => sum + groups[group].filter((item) => item.status === 'rr').length, 0)
  );

  $effect(() => {
    if (equipmentId === null) return;
    loading = true;
    api
      .getEquipment(equipmentId)
      .then((response) => {
        const inspection = response.equipment_inspection;
        siteId = inspection.site_id;
        company = inspection.company;
        location = inspection.location;
        jobNo = inspection.job_no;
        equipmentNo = inspection.equipment_no;
        hourMeter = inspection.hour_meter;
        inspectionDate = inspection.inspection_date;
        shift = inspection.shift;
        jobName = inspection.job_name;
        equipmentType = inspection.equipment_type;
        mileage = inspection.mileage;
        defectsExplanation = inspection.defects_explanation;
        repairsNeeded = inspection.repairs_needed;
        repairsNotNeeded = inspection.repairs_not_needed;
        operatorSignature = inspection.operator_signature;
        operatorDate = inspection.operator_date;
        repairsCompletedBy = inspection.repairs_completed_by;
        repairsCompletedDate = inspection.repairs_completed_date;
        mechanicSignature = inspection.mechanic_signature;
        mechanicDate = inspection.mechanic_date;

        const next = emptyGroups();
        for (const item of response.items) {
          next[item.group_name] = [...next[item.group_name], { label: item.label, status: item.status }];
        }
        groups = next;
        savedId = inspection.id;
        // The loaded document is the auto-save baseline: nothing is dirty yet.
        lastSaved = JSON.stringify(body());
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that equipment inspection');
        error = 'Could not load that equipment inspection.';
      })
      .finally(() => {
        loading = false;
      });
  });

  function addItem(group: EquipmentChecklistGroup) {
    groups[group] = [...groups[group], { label: '', status: 'pending' }];
  }

  function removeItem(group: EquipmentChecklistGroup, index: number) {
    groups[group] = groups[group].filter((_, i) => i !== index);
  }

  function moveItem(group: EquipmentChecklistGroup, index: number, delta: number) {
    const rows = groups[group];
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    groups[group] = next;
  }

  function itemsForSave(): EquipmentInspectionItemInput[] {
    const out: EquipmentInspectionItemInput[] = [];
    for (const group of GROUP_ORDER) {
      for (const row of groups[group]) {
        out.push({ group_name: group, label: row.label, status: row.status });
      }
    }
    return out;
  }

  function body(): SaveEquipmentInspectionRequest {
    return {
      site_id: siteId,
      company,
      location,
      job_no: jobNo,
      equipment_no: equipmentNo,
      hour_meter: hourMeter,
      inspection_date: inspectionDate,
      shift,
      job_name: jobName,
      equipment_type: equipmentType,
      mileage,
      defects_explanation: defectsExplanation,
      repairs_needed: repairsNeeded,
      repairs_not_needed: repairsNotNeeded,
      operator_signature: operatorSignature,
      operator_date: operatorDate,
      repairs_completed_by: repairsCompletedBy,
      repairs_completed_date: repairsCompletedDate,
      mechanic_signature: mechanicSignature,
      mechanic_date: mechanicDate,
      items: itemsForSave()
    };
  }

  function chooseSite(site: SiteSummary | null) {
    siteId = site?.id ?? null;
    if (site) jobName = site.name;
  }

  // Opened from a site page ("New inspection for Demo"): pre-select that
  // site, fill the job name from it, and carry across the job-level details
  // shared by every machine on the site — company, location, job number —
  // taken from the site's most recent inspection. Only ever on a NEW form,
  // and only into fields still empty, so this can never overwrite something
  // typed or loaded.
  //
  // Equipment number, equipment type, hour meter and mileage are
  // deliberately NOT copied. They identify the machine and the moment, and
  // carrying one forward would file an inspection against the wrong
  // machine — with nothing on the form or the printed copy looking wrong.
  $effect(() => {
    const fromSite = siteIdFromQuery($appLocation.query);
    if (equipmentId === null && fromSite !== null && siteId === null) {
      siteId = fromSite;
      api
        .getSite(fromSite)
        .then((response) => {
          if (!jobName.trim()) jobName = response.site.name;
        })
        .catch(() => {
          // The typed job name text still saves even if the name lookup fails.
        });
      api
        .listEquipment(fromSite)
        .then((response) => {
          const latest = [...response.equipment_inspections].sort((a, b) =>
            b.inspection_date === a.inspection_date
              ? b.updated_at.localeCompare(a.updated_at)
              : b.inspection_date.localeCompare(a.inspection_date)
          )[0];
          if (latest) {
            if (!company.trim()) company = latest.company;
            if (!location.trim()) location = latest.location;
            if (!jobNo.trim()) jobNo = latest.job_no;
          }
        })
        .catch(() => {
          // A convenience only; losing it must not stop a safety check.
        });

      // Started from inside a particular day's inspections, so that is the
      // day this one is for. The date is the one thing above that is NOT
      // job-level, which is why it comes from the URL rather than from the
      // previous record — copying the last inspection's date would quietly
      // file today's check under an older day.
      const fromDate = ($appLocation.query.get('date') ?? '').trim();
      if (fromDate && !inspectionDate.trim()) inspectionDate = fromDate;
    }
  });

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    let seq = 0;
    const started = printEquipment(
      {
        equipment_inspection: {
          id: savedId ?? 0,
          company,
          location,
          job_no: jobNo,
          site_id: siteId,
          equipment_no: equipmentNo,
          hour_meter: hourMeter,
          inspection_date: inspectionDate,
          shift,
          job_name: jobName,
          equipment_type: equipmentType,
          mileage,
          defects_explanation: defectsExplanation,
          repairs_needed: repairsNeeded,
          repairs_not_needed: repairsNotNeeded,
          operator_signature: operatorSignature,
          operator_date: operatorDate,
          repairs_completed_by: repairsCompletedBy,
          repairs_completed_date: repairsCompletedDate,
          mechanic_signature: mechanicSignature,
          mechanic_date: mechanicDate,
          created_at: stamp,
          updated_at: stamp
        },
        items: GROUP_ORDER.flatMap((group) =>
          groups[group].map((item) => {
            seq += 1;
            return {
              id: seq,
              inspection_id: savedId ?? 0,
              seq,
              group_name: group,
              label: item.label,
              status: item.status
            };
          })
        ),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save(opts: { auto?: boolean } = {}) {
    if (saving || removed) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const response =
        savedId === null
          ? await api.createEquipment(body())
          : await api.updateEquipment(savedId, body());

      const isNew = savedId === null;
      savedId = response.equipment_inspection.id;
      // The baseline is what the form NOW holds, so nothing reads as a fresh edit.
      lastSaved = JSON.stringify(body());
      if (!opts.auto) {
        notify({ tone: 'ok', title: isNew ? 'Equipment inspection created' : 'Equipment inspection saved' });
      }
      // A new inspection gets its own address, so a refresh or a back button
      // lands on the saved sheet rather than a blank one. An AUTO save only
      // rewrites the address bar: `navigate` would change the route param and
      // the {#key} around this screen would tear the form down mid-keystroke.
      if (isNew) {
        if (opts.auto) claimAddress(routePath.qualityEquipment(savedId));
        else navigate(routePath.qualityEquipment(savedId), { replace: true, keepScroll: true });
      }
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /* --- auto-save (lib/autosave.ts; same wiring as DailyReportForm) -------- */

  /** JSON of the document at its last save/load — the dirty check's anchor. */
  let lastSaved = $state<string | null>(null);
  /** Set once the inspection is deleted, so no timer can quietly resurrect it. */
  let removed = false;

  /**
   * Does a NEW form hold something worth creating a record for? Everything
   * the site prefill can fill — siteId, jobName, company, location, jobNo,
   * and the date carried in the URL — deliberately does not count, and
   * neither does the seeded checklist while every item is still pending.
   * Only something a person typed or ticked.
   */
  function hasContent(): boolean {
    return (
      equipmentNo.trim() !== '' ||
      hourMeter.trim() !== '' ||
      shift.trim() !== '' ||
      equipmentType.trim() !== '' ||
      mileage.trim() !== '' ||
      defectsExplanation.trim() !== '' ||
      repairsNeeded.trim() !== '' ||
      repairsNotNeeded.trim() !== '' ||
      operatorSignature.trim() !== '' ||
      operatorDate.trim() !== '' ||
      repairsCompletedBy.trim() !== '' ||
      repairsCompletedDate.trim() !== '' ||
      mechanicSignature.trim() !== '' ||
      mechanicDate.trim() !== '' ||
      GROUP_ORDER.some((group) => groups[group].some((item) => item.status !== 'pending'))
    );
  }

  const dirty = $derived(lastSaved !== null && JSON.stringify(body()) !== lastSaved);

  const autosave = createAutosave({
    snapshot: () => JSON.stringify(body()),
    saved: () => lastSaved,
    busy: () => saving || deleting,
    blocked: () => removed,
    ready: () => savedId !== null || hasContent(),
    save: () => void save({ auto: true })
  });

  $effect(() => {
    const snapshot = JSON.stringify(body()); // subscribes to every field
    if (loading) return;
    if (snapshot === lastSaved) return;
    autosave.changed();
  });

  // A timer must not outlive the form (navigation away mid-edit).
  $effect(() => {
    return () => autosave.stop();
  });

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteEquipment(savedId);
      // No auto-save timer may quietly resurrect what was just deleted.
      removed = true;
      autosave.stop();
      notify({ tone: 'ok', title: 'Equipment inspection deleted' });
      navigate(routePath.qualityEquipmentList(siteId ?? undefined), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that equipment inspection');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

<!-- Enter moves to the next field; see lib/fields/advance-on-enter.ts. -->
<div use:advanceOnEnter>
{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <div class="titlebar">
        <h1 class="card__title">
          {savedId === null ? 'New equipment inspection' : equipmentNo || 'Equipment inspection'}
        </h1>
        <a class="btn btn--sm" href={routePath.qualityEquipmentList(siteId ?? undefined)} onclick={linkHandler}>
          All equipment inspections
        </a>
      </div>

      <SitePicker value={siteId} onchoose={chooseSite} />

      <div class="grid2">
        <label class="field">
          <span class="label">Company</span>
          <input type="text" bind:value={company} />
        </label>
        <label class="field">
          <span class="label">Location</span>
          <input type="text" bind:value={location} />
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Job #</span>
          <input type="text" bind:value={jobNo} />
        </label>
        <label class="field">
          <span class="label">Equipment #</span>
          <input type="text" bind:value={equipmentNo} />
          {#if fieldErrors.equipment_no}<span class="field__flag">{fieldErrors.equipment_no}</span>{/if}
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Hour meter</span>
          <input type="text" inputmode="decimal" bind:value={hourMeter} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={inspectionDate} />
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Shift</span>
          <input type="text" bind:value={shift} />
        </label>
        <label class="field">
          <span class="label">Job name</span>
          <!--
            Prints on the sheet. Choosing a site above fills it in; it stays
            free text so a record from before sites, or a site not on the
            list yet, is never blocked or overwritten against the user's
            wishes.
          -->
          <input type="text" bind:value={jobName} placeholder="Kiewit WF" />
          <span class="field__hint">Filled in from the site; edit only if the sheet should say something different.</span>
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Type</span>
          <input type="text" bind:value={equipmentType} />
        </label>
        <label class="field">
          <span class="label">Mileage</span>
          <input type="text" inputmode="decimal" bind:value={mileage} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Checklist</h2>
      <p class="meta">{EQUIPMENT_CHECKLIST_LEGEND}</p>
      <!-- A plain count, not a pill and not coloured — this form judges nothing,
           but it's still what a reader of this document looks for first. -->
      <p class="meta"><strong>{rrCount}</strong> item{rrCount === 1 ? '' : 's'} marked Requires Repair.</p>
    </div>
  </div>

  {#each GROUP_ORDER as group (group)}
    <div class="card">
      <div class="card__body">
        <h2 class="card__title">{EQUIPMENT_CHECKLIST_GROUP_LABELS[group]}</h2>

        <div class="rows rows--checklist">
          <div class="rows__head" aria-hidden="true">
            <span>Item</span>
            <span>Status</span>
            <span></span>
          </div>

          {#each groups[group] as item, index (index)}
            <div class="row">
              <div class="row__fields">
                <label class="row__field">
                  <span class="label">Item</span>
                  <input
                    type="text"
                    bind:value={item.label}
                    aria-label="{EQUIPMENT_CHECKLIST_GROUP_LABELS[group]} item {index + 1} name"
                  />
                </label>

                <div class="row__field row__field--status">
                  <span class="label">Status</span>
                  <div
                    class="segmented"
                    role="group"
                    aria-label="Status for {item.label || `item ${index + 1}`}"
                  >
                    {#each STATUS_CHOICES as choice (choice.value)}
                      <button
                        type="button"
                        class={choice.cls}
                        aria-pressed={item.status === choice.value}
                        onclick={() => (item.status = choice.value)}
                      >
                        {choice.label}
                      </button>
                    {/each}
                  </div>
                </div>
              </div>

              <div class="row__actions">
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => moveItem(group, index, -1)}
                  disabled={index === 0}
                  aria-label="Move {item.label || `item ${index + 1}`} up"
                >↑</button>
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => moveItem(group, index, 1)}
                  disabled={index === groups[group].length - 1}
                  aria-label="Move {item.label || `item ${index + 1}`} down"
                >↓</button>
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => removeItem(group, index)}
                  aria-label="Remove {item.label || `item ${index + 1}`}"
                >✕</button>
              </div>
            </div>
          {/each}

          {#if groups[group].length === 0}
            <p class="meta">No items in this group.</p>
          {/if}
        </div>

        <button class="btn btn--block" type="button" onclick={() => addItem(group)}>Add item</button>
      </div>
    </div>
  {/each}

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Explanation of defects</span>
        <textarea rows="4" bind:value={defectsExplanation}></textarea>
      </label>

      <label class="field">
        <span class="label">Repairs needed</span>
        <input type="text" bind:value={repairsNeeded} />
      </label>

      <label class="field">
        <span class="label">Repairs not needed</span>
        <input type="text" bind:value={repairsNotNeeded} />
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Signatures</h2>

      <div class="grid2">
        <label class="field">
          <span class="label">Operator signature</span>
          <input type="text" bind:value={operatorSignature} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={operatorDate} />
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Repairs completed by</span>
          <input type="text" bind:value={repairsCompletedBy} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={repairsCompletedDate} />
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Mechanic signature</span>
          <input type="text" bind:value={mechanicSignature} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={mechanicDate} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={() => save()}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save equipment inspection{/if}
      </button>

      <!-- The auto-save's word on where things stand. Only ever informational:
           the button above still saves on demand. -->
      <p class="autosave" role="status">
        {#if saving}
          Saving…
        {:else if savedId !== null && !dirty}
          All changes saved.
        {:else if savedId !== null || hasContent()}
          Unsaved changes — saving automatically…
        {:else}
          Saves automatically as you fill it in.
        {/if}
      </p>

      {#if $isAdmin && savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this equipment inspection?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete equipment inspection
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}
</div>

<style>
  /* Informational, never alarming: where the auto-save stands. */
  .autosave {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
    text-align: center;
  }

  .titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .titlebar .card__title {
    margin: 0;
  }

  /*
    Same shared-column mechanism as DailyReportForm's .rows--person /
    .rows--delivery / .rows--activity: one column template string, set once
    per group as --rowcols on the .rows wrapper, read by both .rows__head and
    every .row so heading and data columns can never drift apart. Below
    60rem each item stacks (label, then the segmented status control,
    full-width); at and above 60rem the row becomes one line.

    This table is far lighter than the activity table the same pattern
    protects — two data columns (an item name and a 3-button status picker)
    plus the action column — so there is no floor-tuning tightrope here: at
    the 60rem/960px breakpoint itself, measured the same way (an iframe
    sized to exactly 960px CSS width, see the task report), the row's floors
    plus gaps plus the action column sum to a little over 500px against
    roughly 875-890px actually available inside the office container at that
    width, several hundred px of slack even at the tightest point.
  */
  .rows--checklist {
    --rowcols: minmax(10rem, 1fr) 9rem calc(3 * var(--tap) + 0.6rem);
  }

  .row__fields {
    display: grid;
    gap: 0.6rem;
  }

  .row__field {
    display: block;
    min-width: 0;
  }

  .rows__head {
    display: none;
  }

  .row {
    border-bottom: 1px solid var(--line-soft);
    padding: 0.85rem 0;
  }

  .row:first-child {
    padding-top: 0;
  }

  .row:last-of-type {
    border-bottom: 0;
  }

  .row__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    margin-top: 0.6rem;
  }

  @media (min-width: 60rem) {
    .rows__head {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      padding: 0 0 0.4rem;
      border-bottom: 2px solid var(--line);
    }

    .rows__head span {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--ink-soft);
    }

    .row {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      align-items: center;
      padding: 0.4rem 0;
    }

    /* The wrapper drops out of layout so its two children — the item name
       and the status control — take their place directly as the row's own
       leading columns, in the document order they already had — same
       mechanism as MixDesignForm's .material__fields. */
    .row__fields {
      display: contents;
    }

    .row__field .label {
      display: none;
    }

    .row__actions {
      justify-content: flex-start;
      margin-top: 0;
    }
  }
</style>
